interface RecheckErrorDto {
    error?: {
        errorCode?: string;
        message?: string;
    };
}

const HTTP_NOT_FOUND = 404;

export const GENERIC_RECHECK_ERROR_TRANSLATION_KEY = "Autopilot.recheckModal.errorMessage";
const NOTHING_TO_RECHECK_TRANSLATION_KEY = "Autopilot.recheckModal.nothingToRecheck";

type RecheckErrorCode = "NOTHING_TO_RECHECK" | "LICENSE_EXPIRED" | "NOT_ENOUGH_LICENSES";

const ERROR_CODE_TRANSLATION_KEYS: Record<RecheckErrorCode, string> = {
    NOTHING_TO_RECHECK: NOTHING_TO_RECHECK_TRANSLATION_KEY,
    LICENSE_EXPIRED: "Autopilot.recheckModal.licenseExpired",
    NOT_ENOUGH_LICENSES: "Autopilot.recheckModal.notEnoughLicenses",
};

/**
 * Resolve the translation key of the message to show when an Autopilot recheck request fails.
 */
export function resolveRecheckErrorTranslationKey(error: unknown): string {
    if (!(error instanceof Error)) {
        return GENERIC_RECHECK_ERROR_TRANSLATION_KEY;
    }
    const errorCode = parseErrorCode(error.message);
    if (errorCode !== undefined) {
        return isKnownErrorCode(errorCode)
            ? ERROR_CODE_TRANSLATION_KEYS[errorCode]
            : GENERIC_RECHECK_ERROR_TRANSLATION_KEY;
    }
    // Backends deployed before error codes were introduced respond with HTTP 404 and no error code when there's
    // nothing to recheck. Falling back to the status lets this frontend be deployed independently of the backend.
    if ("status" in error && error.status === HTTP_NOT_FOUND) {
        return NOTHING_TO_RECHECK_TRANSLATION_KEY;
    }
    return GENERIC_RECHECK_ERROR_TRANSLATION_KEY;
}

// Own-property check prevents inherited keys such as "toString" from resolving to Object.prototype members.
function isKnownErrorCode(errorCode: string): errorCode is RecheckErrorCode {
    return Object.prototype.hasOwnProperty.call(ERROR_CODE_TRANSLATION_KEYS, errorCode);
}

function parseErrorCode(responseBody: string): string | undefined {
    try {
        const dto: RecheckErrorDto | null = JSON.parse(responseBody);
        return dto?.error?.errorCode;
    } catch {
        return undefined;
    }
}
